import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Percent } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Badge } from '@/components/common/Badge';
import { Modal } from '@/components/common/Modal';
import { SelectField, TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { discountsApi, feeCategoriesApi } from '@/services/api/endpoints/finance';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { ApprovalStatus, Discount, DiscountKind } from '@/types/finance';
import type { UUID } from '@/types/domain';
import { formatDate } from '@/utils/format';
import { ApprovalBadge } from './shared';
import { ApprovalDialog } from './ApprovalDialog';
import { StudentPicker } from './StudentPicker';

const KINDS: { value: DiscountKind; label: string; hint: string }[] = [
  { value: 'STUDENT', label: 'For one student', hint: 'A concession agreed with one family.' },
  { value: 'SIBLING', label: 'Sibling concession', hint: 'A reduction for a second or later child.' },
  { value: 'EARLY_PAYMENT', label: 'Early payment', hint: 'Earned by paying before the due date. Applied when the money arrives, not when the bill is raised.' },
  { value: 'FEE_CATEGORY', label: 'On a kind of fee', hint: 'Applies to particular fee categories.' },
  { value: 'CUSTOM', label: 'Something else', hint: '' },
];

const today = () => new Date().toISOString().slice(0, 10);

/** Reductions in what is charged, and who agreed to them. */
export function DiscountsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('createdAt,desc');

  const [approvalFilter, setApprovalFilter] = useState<ApprovalStatus | ''>('');
  const [open, setOpen] = useState(false);
  const [deciding, setDeciding] = useState<Discount | null>(null);
  const [student, setStudent] = useState<{ id: UUID; name: string; number?: string } | null>(null);
  const [form, setForm] = useState({
    name: '', discountKind: 'STUDENT' as DiscountKind, calculation: 'PERCENTAGE',
    value: '', maxAmount: '', reason: '', effectiveFrom: today(), effectiveTo: '',
    earlyPaymentDays: '', feeCategoryIds: [] as UUID[],
  });

  const params = {
    ...table.params,
    ...(approvalFilter !== '' ? { approvalStatus: approvalFilter } : {}),
  };

  const query = useQuery({
    queryKey: queryKeys.discounts.list(params),
    queryFn: () => discountsApi.list(params),
  });

  const categories = useQuery({
    queryKey: queryKeys.feeCategories.list(true),
    queryFn: () => feeCategoriesApi.list(true),
    enabled: open,
  });

  const propose = useMutation({
    mutationFn: () =>
      discountsApi.create({
        name: form.name,
        discountKind: form.discountKind,
        calculation: form.calculation,
        value: Number(form.value),
        ...(form.maxAmount !== '' ? { maxAmount: Number(form.maxAmount) } : {}),
        ...(student !== null ? { studentId: student.id } : {}),
        reason: form.reason,
        effectiveFrom: form.effectiveFrom,
        ...(form.effectiveTo !== '' ? { effectiveTo: form.effectiveTo } : {}),
        ...(form.discountKind === 'EARLY_PAYMENT' && form.earlyPaymentDays !== ''
          ? { earlyPaymentDays: Number(form.earlyPaymentDays) } : {}),
        ...(form.feeCategoryIds.length > 0 ? { feeCategoryIds: form.feeCategoryIds } : {}),
      }),
    onSuccess: () => {
      toast.success('Proposed — it reduces nothing until approved');
      setOpen(false);
      setStudent(null);
      setForm({
        name: '', discountKind: 'STUDENT', calculation: 'PERCENTAGE', value: '', maxAmount: '',
        reason: '', effectiveFrom: today(), effectiveTo: '', earlyPaymentDays: '', feeCategoryIds: [],
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.discounts.all });
    },
    onError: (error) => handleApiError(error),
  });

  const decide = useMutation({
    mutationFn: ({ id, approved, reason }: { id: string; approved: boolean; reason: string }) =>
      discountsApi.decide(id, { approved, ...(reason.trim() !== '' ? { reason } : {}) }),
    onSuccess: (updated) => {
      toast.success(updated.approvalStatus === 'APPROVED' ? 'Discount approved' : 'Discount refused');
      setDeciding(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.discounts.all });
    },
    onError: (error) => { setDeciding(null); handleApiError(error); },
  });

  const columns: Column<Discount>[] = [
    {
      key: 'name',
      header: 'Discount',
      sortKey: 'name',
      render: (d) => (
        <div>
          <span className="font-medium text-ink">{d.name}</span>
          <span className="block text-[11px] text-muted">
            {KINDS.find((k) => k.value === d.discountKind)?.label ?? d.discountKind}
          </span>
        </div>
      ),
    },
    {
      key: 'who',
      header: 'Applies to',
      render: (d) => (
        <div className="text-[13px]">
          <span className="text-ink">{d.studentName ?? 'Every student'}</span>
          <span className="block text-[11px] text-muted">
            {d.feeCategoryNames.length === 0 ? 'every fee' : d.feeCategoryNames.join(', ')}
          </span>
        </div>
      ),
    },
    {
      key: 'value',
      header: 'Worth',
      sortKey: 'value',
      className: 'text-right',
      render: (d) => (
        <div className="text-right">
          <span className="block tabular-nums text-ink">
            {d.calculation === 'PERCENTAGE' ? `${d.value}%` : d.value.toFixed(2)}
          </span>
          {d.maxAmount !== undefined && (
            <span className="block text-[10.5px] text-subtle">capped at {d.maxAmount}</span>
          )}
        </div>
      ),
    },
    {
      key: 'window',
      header: 'In force',
      render: (d) => (
        <div className="whitespace-nowrap text-[13px]">
          <span className="tabular-nums text-ink">{formatDate(d.effectiveFrom)}</span>
          <span className="block text-[11px] text-muted">
            {d.effectiveTo === undefined ? 'ongoing' : `until ${formatDate(d.effectiveTo)}`}
          </span>
        </div>
      ),
    },
    {
      key: 'applied',
      header: 'Used',
      className: 'text-right',
      render: (d) => <span className="block text-right tabular-nums text-muted">{d.timesApplied}</span>,
    },
    {
      key: 'approval',
      header: 'Approval',
      render: (d) => (
        <div className="flex flex-wrap items-center gap-1.5">
          <ApprovalBadge status={d.approvalStatus} />
          {d.approvalStatus === 'APPROVED' && !d.inForce && (
            <Badge tone="neutral">not in force</Badge>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (d) =>
        d.approvalStatus === 'PENDING' && can(PERMISSIONS.DISCOUNT_APPROVE) ? (
          <Button size="sm" onClick={() => setDeciding(d)}>Decide</Button>
        ) : null,
    },
  ];

  const kindHint = KINDS.find((k) => k.value === form.discountKind)?.hint ?? '';

  return (
    <>
      <ListPage
        title="Discounts"
        description="Reductions in what a family is charged. Nothing pending reduces a bill — approval is a separate act by a separate person."
        icon={<Percent className="h-5 w-5" aria-hidden="true" />}
        action={
          can(PERMISSIONS.DISCOUNT_MANAGE) ? (
            <Button onClick={() => setOpen(true)}>Propose a discount</Button>
          ) : undefined
        }
        filters={
          <FilterSelect label="Approval" value={approvalFilter}
                        onChange={(value) => { setApprovalFilter(value); table.setPage(0); }}
                        options={[
                          { value: 'PENDING', label: 'Awaiting a decision' },
                          { value: 'APPROVED', label: 'Approved' },
                          { value: 'REJECTED', label: 'Refused' },
                        ]} />
        }
        pagination={query.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={query.data?.data}
          rowKey={(d) => d.id}
          loading={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          emptyTitle="No discounts"
          emptyDescription="Propose one when a family is given a concession."
          emptyIcon={<Percent className="h-6 w-6" aria-hidden="true" />}
        />
      </ListPage>

      <Modal
        open={open}
        size="lg"
        title="Propose a discount"
        description="It is created awaiting a decision, whoever proposes it."
        onClose={() => { setOpen(false); setStudent(null); }}
        footer={
          <>
            <Button variant="secondary" onClick={() => { setOpen(false); setStudent(null); }}>
              Cancel
            </Button>
            <Button loading={propose.isPending}
                    disabled={form.name === '' || form.value === '' || form.reason === ''}
                    onClick={() => propose.mutate()}>
              Propose
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField label="Name" required placeholder="Sibling concession"
                       value={form.name}
                       onChange={(event) => setForm((f) => ({ ...f, name: event.target.value }))} />
            <SelectField label="Kind" required value={form.discountKind} hint={kindHint}
                         onChange={(event) =>
                           setForm((f) => ({ ...f, discountKind: event.target.value as DiscountKind }))}>
              {KINDS.map((kind) => (
                <option key={kind.value} value={kind.value}>{kind.label}</option>
              ))}
            </SelectField>
          </div>

          {(form.discountKind === 'STUDENT' || form.discountKind === 'SIBLING') && (
            <StudentPicker value={student} onChange={setStudent} label="Student" />
          )}

          <div className="grid gap-4 sm:grid-cols-3">
            <SelectField label="Worked out as" required value={form.calculation}
                         onChange={(event) => setForm((f) => ({ ...f, calculation: event.target.value }))}>
              <option value="PERCENTAGE">A percentage</option>
              <option value="FIXED">A fixed amount</option>
            </SelectField>
            <TextField label={form.calculation === 'PERCENTAGE' ? 'Percentage' : 'Amount'}
                       type="number" step="0.01" required value={form.value}
                       onChange={(event) => setForm((f) => ({ ...f, value: event.target.value }))} />
            <TextField label="Cap" type="number" step="0.01" value={form.maxAmount}
                       hint="Optional ceiling on a percentage."
                       onChange={(event) => setForm((f) => ({ ...f, maxAmount: event.target.value }))} />
          </div>

          {form.discountKind === 'EARLY_PAYMENT' && (
            <TextField label="Days before the due date" type="number" min="1" required
                       value={form.earlyPaymentDays}
                       hint="Payment must arrive at least this many days early to earn it."
                       onChange={(event) => setForm((f) => ({ ...f, earlyPaymentDays: event.target.value }))} />
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <TextField label="In force from" type="date" required value={form.effectiveFrom}
                       onChange={(event) => setForm((f) => ({ ...f, effectiveFrom: event.target.value }))} />
            <TextField label="Until" type="date" value={form.effectiveTo}
                       hint="Blank means indefinitely."
                       onChange={(event) => setForm((f) => ({ ...f, effectiveTo: event.target.value }))} />
          </div>

          <fieldset>
            <legend className="mb-1.5 text-xs font-medium text-muted">
              Which fees it covers
            </legend>
            <p className="mb-2 text-xs text-subtle">
              Choose none to cover every fee. A percentage applies to the charges it covers, not
              to the whole bill.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {(categories.data ?? []).map((category) => {
                const chosen = form.feeCategoryIds.includes(category.id);
                return (
                  <button key={category.id} type="button"
                          onClick={() => setForm((f) => ({
                            ...f,
                            feeCategoryIds: chosen
                              ? f.feeCategoryIds.filter((id) => id !== category.id)
                              : [...f.feeCategoryIds, category.id],
                          }))}
                          className={`border px-2.5 py-1 text-xs transition-colors ${
                            chosen ? 'border-accent bg-accent-soft text-accent-soft-fg'
                                   : 'border-line bg-surface text-muted hover:border-line-strong'}`}>
                    {category.name}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <TextField label="Reason" required placeholder="Second child at the school"
                     hint="Required. An unexplained reduction in a family's bill is exactly what an audit asks about."
                     value={form.reason}
                     onChange={(event) => setForm((f) => ({ ...f, reason: event.target.value }))} />
        </div>
      </Modal>

      <ApprovalDialog
        open={deciding !== null}
        onClose={() => setDeciding(null)}
        pending={decide.isPending}
        title={deciding === null ? '' : `${deciding.name}`}
        summary={
          deciding === null
            ? ''
            : `${deciding.calculation === 'PERCENTAGE' ? `${deciding.value}%` : deciding.value.toFixed(2)} for ${deciding.studentName ?? 'every student'} — ${deciding.reason}`
        }
        onDecide={(approved, reason) =>
          deciding !== null && decide.mutate({ id: deciding.id, approved, reason })
        }
      />
    </>
  );
}
